import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const onChange = () => setMatches(m.matches);
    m.addEventListener ? m.addEventListener("change", onChange) : m.addListener(onChange);
    return () => (m.removeEventListener ? m.removeEventListener("change", onChange) : m.removeListener(onChange));
  }, [query]);
  return matches;
}

const FONT =
  '"Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"';

export default function AdminLayout() {
  const nav = useNavigate();
  const adminName = localStorage.getItem("admin_name") || "Admin";
  const isMobile = useMediaQuery("(max-width: 980px)");
  const [open, setOpen] = useState(false);

  const initials = useMemo(() => {
    const parts = String(adminName || "").trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "A";
    return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
  }, [adminName]);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin_name");
    nav("/admin/login");
  };

  useEffect(() => {
    if (!isMobile) setOpen(false);
  }, [isMobile]);

  return (
    <div style={styles.shell}>
      {isMobile && open ? <div style={styles.overlay} onClick={() => setOpen(false)} /> : null}

      <aside
        style={{
          ...styles.sidebar,
          ...(isMobile
            ? {
                position: "fixed",
                left: 0,
                top: 0,
                height: "100vh",
                transform: open ? "translateX(0)" : "translateX(-110%)",
                transition: "transform 220ms ease",
                zIndex: 50,
                boxShadow: "0 28px 70px rgba(0,0,0,0.30)",
              }
            : {}),
        }}
      >
        <div style={styles.brand}>
          <div style={styles.brandIcon}>📘</div>
          <div style={{ minWidth: 0 }}>
            <div style={styles.brandTitle}>Biography</div>
            <div style={styles.brandSub}>Admin Console</div>
          </div>
        </div>

        <div style={styles.sep} />

        <nav style={styles.nav}>
          <SideLink to="/admin/dashboard" label="Dashboard" icon="▦" onClick={() => setOpen(false)} />
          <SideLink to="/admin/persons" label="Manage Profiles" icon="👤" onClick={() => setOpen(false)} />
          <SideLink to="/admin/contributions" label="Moderate Contributions" icon="✓" onClick={() => setOpen(false)} />
          <SideLink to="/admin/carousel" label="Carousel" icon="🖼️" onClick={() => setOpen(false)} />
          <SideLink to="/admin/media" label="Media Gallery" icon="🎞️" onClick={() => setOpen(false)} />
      <SideLink to="/admin/settings" label="Site Settings" icon="⚙️" onClick={() => setOpen(false)} />

        </nav>

        <div style={{ marginTop: "auto" }}>
          <div style={styles.sep} />
          <button onClick={logout} style={styles.logoutBtn}>
            Logout
          </button>
        </div>
      </aside>

      <main style={styles.main}>
        <header style={styles.topbar}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            {isMobile ? (
              <button style={styles.menuBtn} onClick={() => setOpen((s) => !s)} aria-label="Open menu">
                ☰
              </button>
            ) : null}

            <div style={{ minWidth: 0 }}>
              <div style={styles.topTitle}>Admin</div>
              <div style={styles.topSub}>Administrator</div>
            </div>
          </div>

          <div style={styles.topRight}>
            <div style={styles.notifPill} title="Notifications">
              1
            </div>

            <div style={styles.userChip}>
              <div style={{ textAlign: "right", lineHeight: 1.1, minWidth: 0 }}>
                <div style={styles.userName} title={adminName}>
                  {adminName}
                </div>
                <div style={styles.userRole}>Admin</div>
              </div>
              <div style={styles.avatar} title={adminName}>
                {initials}
              </div>
            </div>
          </div>
        </header>

        <section style={styles.content}>
          <Outlet />
        </section>
      </main>
    </div>
  );
}

function SideLink({ to, label, icon, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      style={({ isActive }) => ({
        ...styles.navItem,
        background: isActive ? "rgba(255,255,255,0.10)" : "transparent",
        borderLeft: isActive ? "3px solid rgba(130,185,255,0.95)" : "3px solid transparent",
      })}
    >
      <span style={styles.navIcon}>{icon}</span>
      <span style={styles.navText}>{label}</span>
    </NavLink>
  );
}

const styles = {
  shell: {
    display: "flex",
    minHeight: "100vh",
    background: "#f6f7fb",
    fontFamily: FONT,
    fontWeight: 400,
  },

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(2,6,23,0.55)",
    zIndex: 40,
  },

  sidebar: {
    width: 260,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 14,
    background: "linear-gradient(180deg,#0b2b55,#061a34)",
    color: "#fff",
  },

  brand: { display: "flex", alignItems: "center", gap: 10 },
  brandIcon: {
    width: 44,
    height: 44,
    borderRadius: 14,
    display: "grid",
    placeItems: "center",
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.16)",
    flex: "0 0 auto",
  },
  brandTitle: { fontWeight: 700, fontSize: 18, letterSpacing: "-0.2px", lineHeight: 1.1 },
  brandSub: { fontSize: 12, opacity: 0.82, fontWeight: 500 },

  sep: { height: 1, background: "rgba(255,255,255,0.12)" },

  nav: { display: "grid", gap: 8, marginTop: 6 },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 12,
    color: "#fff",
    textDecoration: "none",
    fontWeight: 600,
    opacity: 0.96,
  },
  navIcon: { width: 22, display: "inline-block", opacity: 0.95 },
  navText: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },

  logoutBtn: {
    width: "100%",
    padding: "11px 14px",
    borderRadius: 12,
    border: 0,
    cursor: "pointer",
    fontWeight: 700,
    background: "rgba(255,255,255,0.95)",
    color: "#061a34",
  },

  main: { flex: 1, display: "flex", flexDirection: "column", minWidth: 0 },

  topbar: {
    height: 60,
    padding: "0 clamp(12px, 2vw, 18px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    position: "sticky",
    top: 0,
    zIndex: 10,
    background: "linear-gradient(180deg,#f6f7fb,rgba(246,247,251,0.92))",
    backdropFilter: "blur(8px)",
    borderBottom: "1px solid rgba(226,232,240,0.85)",
  },

  menuBtn: {
    width: 42,
    height: 40,
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 700,
  },

  topTitle: { fontSize: 15, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 },
  topSub: { fontSize: 12, fontWeight: 500, color: "#667085" },

  topRight: { display: "flex", alignItems: "center", gap: 12 },

  notifPill: {
    width: 28,
    height: 28,
    borderRadius: 999,
    background: "#fff",
    border: "1px solid #e5e7eb",
    display: "grid",
    placeItems: "center",
    fontWeight: 700,
    color: "#111827",
  },

  userChip: { display: "flex", alignItems: "center", gap: 10 },
  userName: {
    fontSize: 14,
    fontWeight: 700,
    color: "#0f172a",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 200,
  },
  userRole: { fontSize: 12, fontWeight: 500, color: "#667085" },

  avatar: {
    width: 38,
    height: 38,
    borderRadius: 999,
    background: "linear-gradient(135deg,#ffffff,#eef2ff)",
    border: "1px solid #e5e7eb",
    display: "grid",
    placeItems: "center",
    fontWeight: 700,
    color: "#1d4ed8",
    flex: "0 0 auto",
  },

  content: {
    padding: "clamp(12px, 2vw, 18px)",
    width: "100%",
    minWidth: 0,
  },
};
